import { NgClass, AsyncPipe, CommonModule, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Input, OnDestroy, OnInit, WritableSignal, signal } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  NbCardModule,
  NbSpinnerModule,
  NbButtonModule,
  NbTooltipModule,
  NbInputModule,
  NbSelectModule,
  NbOptionModule,
  NbAccordionModule,
  NbDialogRef,
  NbDialogService,
  NbDatepickerModule,
} from '@nebular/theme';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { KPI } from '../../../../@core/api';
import { BaseFormComponent } from '../base-form/base-form.component';
import { I18nService, InitiativesService, InvestmentScenariiService, InvestmentsService, PerimetersService, ProjectsService } from '../../../../@core/utils';
import { combineLatest, filter, first, map, Observable, of, switchMap, tap } from 'rxjs';
import { arrayToIdMap, toLoadingState } from '../../../../@core/utils/scope.service';
import { Router } from '@angular/router';
import { InvestmentsSelectableListComponent } from '../investments-selectable-list/investments-selectable-list.component';
import { SideFormDialogComponent } from '../side-form-dialog/side-form-dialog.component';
import { CurrencyPipe, ThousandsSeparatorPipe } from '../../../pipes';
import { ProjectGainsFormComponent } from './project-gains-form/project-gains-form.component';
import { InitiativesSelectableListComponent } from '../initiatives-selectable-list/initiatives-selectable-list.component';
import { RightPanelDialogService } from '../../../../@core/utils/right-panel-dialog.service';
import { toDateValue, toIsoMonthStartDate } from '../../../utils/date.utils';

@Component({
  selector: 'app-project-form',
  standalone: true,
  templateUrl: './project-form.component.html',
  styleUrls: ['../base-form/base-form.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NbCardModule,
    NgClass,
    NbSpinnerModule,
    NbButtonModule,
    NbTooltipModule,
    FontAwesomeModule,
    NbInputModule,
    NbSelectModule,
    NbOptionModule,
    NbAccordionModule,
    NbDatepickerModule,
    TranslateModule,
    AsyncPipe,
    CommonModule,
    CurrencyPipe,
    ThousandsSeparatorPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectFormComponent extends BaseFormComponent implements OnInit, OnDestroy {
  public readonly toDateValue = toDateValue;
  private readonly toIsoDate = toIsoMonthStartDate;

  @Input() project: Partial<KPI.Project>;
  @Input() addMode: boolean = false;
  private readonly sideFormLevelService = inject(RightPanelDialogService);

  public title = this.translate.instant('Project-sheet');

  // Lists of available values
  public perimeters$ = this.perimetersService.perimeters$.pipe(first(), toLoadingState<KPI.Perimeter[]>());
  public projectTypes$ = this.projectsService.projectTypes$.pipe(toLoadingState<KPI.ProjectType[]>());
  public statuses$ = this.initiativesService.initiativeStatuses$.pipe(toLoadingState<KPI.InitiativeStatus[]>());

  public minStartDate: string = `2000-01-01`;
  public maxStartDate: string = `2050-01-01`;
  public minEndDate: string = this.minStartDate;
  public maxEndDate: string = this.maxStartDate;

  public selectedPerimeterLabel: string = '';
  public currency: string = '€';

  public totalAmount: WritableSignal<number> = signal(0);
  public area: WritableSignal<number> = signal(0);
  public totalGains: WritableSignal<number> = signal(0);

  constructor(
    protected nbDialogRef: NbDialogRef<BaseFormComponent>,
    protected dialogService: NbDialogService,
    protected translate: TranslateService,
    private formBuilder: FormBuilder,
    private projectsService: ProjectsService,
    private datePipe: DatePipe,
    private perimetersService: PerimetersService,
    private router: Router,
    private investmentsService: InvestmentsService,
    private investmentScenariiService: InvestmentScenariiService,
    private i18nService: I18nService,
    private cdRef: ChangeDetectorRef,
    private initiativesService: InitiativesService,
  ) {
    super(nbDialogRef, dialogService, translate);
    this.currency = this.i18nService.getCurrency();
  }

  public ngOnInit(): void {
    super.ngOnInit();
    this.investmentScenariiService.mainScenario$.pipe(first()).subscribe((scenario) => {
      if (this.project) {

        // Set min and max dates for the other datepicker
        if (this.project.start_date) {
          this.minEndDate = this.project.start_date;
        }

        if (this.project.end_date) {
          this.maxStartDate = this.project.end_date;
        }

        // Set selected perimeter label (site > perimeter)
        if (this.project.perimeter) {
          this.setSelectedPerimeterLabelAndArea(this.project.perimeter);
        }

        // Set total project kWh gains for display
        if (this.project.gains) {
          this.totalGains.set(
            this.project.gains.reduce((acc, gain) => acc + gain.theoretical_savings_full_year, 0),
          );
        }
      }

      // Initial values for form, will be empty if addMode, except for start and end dates
      this.initialValues = {
        label: this.project?.label || '',
        perimeter: this.project?.perimeter || null,
        status: this.project?.instances?.find((i) =>
          i.scenario?.id === scenario.id
        )?.status?.id || null,
        start_date: this.datePipe.transform(this.project?.start_date || new Date(), 'yyyy-MM-dd'),
        end_date: this.datePipe.transform(this.project?.end_date || new Date(), 'yyyy-MM-dd'),
        project_type: this.project?.project_type?.id || null,
        global_gain: this.project?.global_gain || false,
        gains: this.project?.gains?.map((gain) => ({
          id: gain.id,
          energy_type: gain.energy_type || null,
          theoretical_savings_full_year: gain.theoretical_savings_full_year,
          theoretical_savings_first_year: gain.theoretical_savings_first_year,
          theoretical_greenhouse_gas_savings_full_year: gain.theoretical_greenhouse_gas_savings_full_year,
          theoretical_greenhouse_gas_savings_first_year: gain.theoretical_greenhouse_gas_savings_first_year,
        })) || [],
        investments: this.project?.investments.sort((a, b) => a - b) || [],
        comments: this.project?.comments || '',
        project_initiatives: this.project?.project_initiatives?.map(({id}) => id).sort((a, b) => a - b) || [],
      };

      // Build the form with initial values
      this.form = this.formBuilder.group({
        label: [
          { value: this.initialValues?.label || '', disabled: false },
          [Validators.required, Validators.maxLength(255)],
        ],
        perimeter: [
          { value: this.initialValues?.perimeter || null, disabled: false },
          [Validators.required],
        ],
        status: [
          { value: this.initialValues?.status || null, disabled: false },
          [Validators.required],
        ],
        start_date: [
          { value: this.initialValues?.start_date || this.datePipe.transform(new Date(), 'yyyy-MM-dd'), disabled: false },
          [Validators.required],
        ],
        end_date: [
          { value: this.initialValues?.end_date || this.datePipe.transform(new Date(), 'yyyy-MM-dd'), disabled: false },
          [Validators.required],
        ],
        project_type: [
          { value: this.initialValues?.project_type || null, disabled: false },
          [Validators.required],
        ],
        global_gain: [
          { value: this.initialValues?.global_gain || false, disabled: false },
          [Validators.required],
        ],
        gains: this.formBuilder.array(
          this.initialValues.gains.map((gain) => this.formBuilder.group({
            ...gain,
          },
          { validators: [ProjectFormComponent.gainValidator(), Validators.required] },
          )),
        ),
        investments: [[]],
        project_initiatives: [[]],
        comments: [
          { value: this.initialValues?.comments || '', disabled: false },
          [Validators.maxLength(255)],
        ],
      });

      // Replace the default backdrop click close event by a custom one
      this.initializeBackdropClick();

      // Initialize triggered events from the form fields
      this.initializeEvents();

      /* Force update of form initiatial values and validity,
      ** to trigger events and make everything display correctly from the initialisation of the form
      */
      ['investments', 'project_initiatives', 'status'].forEach((key) => {
        this.form.get(key).patchValue(this.initialValues[key], { emitEvent: true });
        this.form.get(key).updateValueAndValidity();
      });
    });
  }

  static gainValidator() {
    return (gain: FormGroup) => {
      const energy = gain.get("energy_type")?.value;
      if (!energy) {
        return { energy: { required: true } };
      }
      return null;
    };
  }

  public openProjectFullPage(): void {
    this.router.navigate(['single-pages', 'project', this.project.id]);
    this.close();
  }

  private initializeEvents(): void {
    this.form.get('start_date').valueChanges.subscribe((startDate) => {
      if (startDate) {
        this.minEndDate = startDate;
      }
    });

    this.form.get('end_date').valueChanges.subscribe((endDate) => {
      if (endDate) {
        this.maxStartDate = endDate;
      }
    });

    // Update displayed site, perimeter and area when perimeter changes
    this.form.get('perimeter').valueChanges.subscribe((selectedPerimeterId) => {
      this.setSelectedPerimeterLabelAndArea(selectedPerimeterId);
    });

    // Update displayed total amount when investments change
    this.form.get('investments').valueChanges.pipe(
      tap((investmentIds: number[]) => {
        if (investmentIds.length) {
          this.form.get('perimeter').disable();
        } else {
          this.form.get('perimeter').enable();
        }
      }),
      switchMap((investmentIds: number[]) => combineLatest(
        investmentIds.length
          ? investmentIds.map((id) => this.investmentsService.getInvestment(id).pipe(first()))
          : [of(null)],
      )),
      map((investments) => investments.reduce((acc, investment) => acc + Number(investment?.current_price) || 0, 0)),
    ).subscribe((totalAmount) => {
      this.totalAmount.set(totalAmount);
    });

    // Update displayed total gains when gains change (Not the gains from the project initiatives)
    this.form.get('gains').valueChanges.pipe(
      map((gains) => gains.reduce((acc, gain) => acc + gain.theoretical_savings_full_year, 0)),
    ).subscribe((totalGains) => {
      this.totalGains.set(totalGains);
    });
  }

  public onProjectDateChange(date: Date, type: 'start' | 'end'): void {
    this.form.get(`${type}_date`)?.setValue(this.toIsoDate(date));
  }

  public formatMonthYear(dateValue: any): string {
    const date = this.toDateValue(dateValue);
    return date ? this.datePipe.transform(date, 'MMMM yyyy') || '-' : '-';
  }

  // Set the selected perimeter label and area to be displayed in the form
  private setSelectedPerimeterLabelAndArea(selectedPerimeterId): void {
    this.getSelectedSiteAndPerimeterFromId(selectedPerimeterId).subscribe(([site, perimeter]) => {
      if (perimeter) {
        this.area.set(perimeter.flooring_area);
      } else {
        this.area.set(0);
      }
      this.selectedPerimeterLabel = `${site ? site?.name + ' > ': ''}${perimeter ? perimeter?.name : ''}`;
    });
  }

  // Returns the selected site and perimeter from the selected perimeter id in the form
  private getSelectedSiteAndPerimeterFromId(selectedPerimeterId): Observable<[KPI.Perimeter, KPI.Perimeter]> {
    return this.perimeters$.pipe(
      filter((res) => !res.loading),
      map((res) => res.data),
      first(),
      map((sites) => {
        const selectedSite = sites.find((site) =>
          !!site.sub_perimeters.find((perimeter) => perimeter.id === selectedPerimeterId)
        );
        const selectedPerimeter = selectedSite?.sub_perimeters.find((perimeter) => perimeter.id === selectedPerimeterId);
        return [selectedSite, selectedPerimeter] as [KPI.Perimeter, KPI.Perimeter];
      }),
    )
  }

  public openInvestmentsList(): void {
    const selectedPerimeterId = this.form.get('perimeter').value;
    if (!selectedPerimeterId) {
      // Show warning if no perimeter selected
      this.warningMissingPerimeter();
    } else {
      combineLatest([
        this.getSelectedSiteAndPerimeterFromId(selectedPerimeterId),
        this.statuses$.pipe(
          filter((res) => !res.loading), map((res) => res.data),
          arrayToIdMap(),
        ),
      ]).subscribe(([[_site, perimeter], statuses]) => {
        this.sideFormLevelService.open(
          InvestmentsSelectableListComponent,
          {
            autoFocus: false,
            closeOnBackdropClick: false,
            context: {
              perimeter,
              // Used only to show up investments outside the years scope
              startYear: new Date(this.form.get('start_date').value).getFullYear(),
              endYear: new Date(this.form.get('end_date').value).getFullYear(),
              // Default selected elements in list
              inputLinkedElementIds: this.form.get('investments').value,
              title: this.translate.instant('Investments linked to the project'),
              // Remove hypothesis investments from list if project status is planned
              removeHypothesis: !!statuses[this.form.get('status').value]?.is_request,
            }
          },
        ).onClose.pipe(first()).subscribe((investmentIds: number[]) => {
          this.form.get('investments').patchValue(investmentIds?.sort((a, b) => a - b), { emitEvent: true });
          this.form.get('investments').updateValueAndValidity();
          this.cdRef.detectChanges();
        });
      });
    }
  }

  public openInitiativesList(): void {
    const selectedPerimeterId = this.form.get('perimeter').value;
    if (!selectedPerimeterId) {
      // Show warning if no perimeter selected
      this.warningMissingPerimeter();
    } else {
      this.getSelectedSiteAndPerimeterFromId(selectedPerimeterId).subscribe(([_site, perimeter]) => {
        this.sideFormLevelService.open(
          InitiativesSelectableListComponent,
          {
            autoFocus: false,
            closeOnBackdropClick: false,
            context: {
              // Used only to show up initiatives outside the years scope
              perimeter,
              startYear: new Date(this.form.get('start_date').value).getFullYear(),
              endYear: new Date(this.form.get('end_date').value).getFullYear(),
              // Default selected elements in list
              inputLinkedElementIds: this.form.get('project_initiatives').value,
              title: this.translate.instant('Initiatives linked to the bouquet of works'),
            }
          },
        ).onClose.pipe(first()).subscribe((initiativeIds: number[]) => {
          this.form.get('project_initiatives').patchValue(initiativeIds?.sort((a, b) => a - b), { emitEvent: true });
          this.form.get('project_initiatives').updateValueAndValidity();
          this.cdRef.detectChanges();
        });
      });
    }
  }

  private warningMissingPerimeter(): void {
    this.dialogService.open(
      SideFormDialogComponent,
      {
        autoFocus: false,
        context: {
          title: this.translate.instant('Warning'),
          content: this.translate.instant('Please select a perimeter first'),
        }
      },
    );
  }

  public openGainsForm(): void {
    this.sideFormLevelService.open(
      ProjectGainsFormComponent,
      {
        autoFocus: false,
        closeOnBackdropClick: false,
        context: {
          projectForm: this.form,
        },
      },
    ).onClose.subscribe(() => this.cdRef.detectChanges());
  }

  public anyFieldValueChanged(): boolean {
    const anyFieldChanged = Object.keys(this.initialValues).some((key: string) =>
      this.fieldValueChanged(key),
    );
    return anyFieldChanged || this.addMode;
  }

  public isFormValid(): boolean {
    return this.form.valid;
  }

  public onSubmit(): void {
    this.loading = true;
    const excludeFields = ['status', 'gains', 'investments', 'project_initiatives'];
    const changes: {[key: string]: any} = Object.keys(this.initialValues)
      .filter((key) => this.fieldValueChanged(key))
      .reduce((acc, key) => ({ ...acc, [key]: this.form.get(key).value }), {});

    // Manage simple field changes
    const projectChanges: Partial<KPI.Project> = Object.keys(changes)
      .filter((key) => !excludeFields.includes(key))
      .reduce((acc, key) => ({
        ...acc,
        [key as keyof KPI.Project]: key === 'start_date' || key === 'end_date'
          ? this.datePipe.transform(this.form.get(key).value, "yyyy-MM-dd")
          : this.form.get(key).value
      }), {});

    const statusChanges: number = changes['status'];
    const gainsChanges: any[] = changes['gains'];
    const investmentsChanges: number[] = changes['investments'];
    const initiativesChanges: number[] = changes['project_initiatives'];

    let request: Observable<any> = of(null);
    // -- CASE 1: UPDATE PROJECT
    if (!this.addMode && this.project?.id) {
      // Prepare request to update base project fields
      if (Object.entries(projectChanges)?.length) {
        request = this.projectsService.updateProject(this.project.id, projectChanges).pipe(first());
      }
    // -- CASE 2: CREATE PROJECT
    } else {
      // Prepare request to create project object
      request = this.projectsService.createProject(
        Object.keys(this.form.controls)
          .filter((key) => !excludeFields.includes(key))
          .reduce((acc, key) => ({
            ...acc,
            [key as keyof KPI.Project]: key === 'start_date' || key === 'end_date'
              ? this.datePipe.transform(this.form.get(key).value, "yyyy-MM-dd")
              : this.form.get(key).value
          }), {}),
      ).pipe(first());
    }

    /**
     * Execute a sequence of requests:
     * 1. Update or Create project
     * 2. Update project main scenario instance status if any changes
     * 3. Update gains if any changes
     * 4. Update investments and initiatives if any changes
     * 5. Refresh projects list
     */
    request.pipe(
      switchMap((project) =>
        combineLatest(
          this.generateUpdateRequests(
            this.project?.id || project?.id,
            statusChanges,
            gainsChanges,
            investmentsChanges,
            initiativesChanges
          ),
        ),
      ),
      first(),
    ).subscribe(() => {
      this.projectsService.refreshProjects();
      this.close();
    });
  }

  private generateUpdateRequests(
    projectId: number,
    statusChanges: number,
    gainsChanges: any[],
    investmentsChanges: number[],
    initiativesChanges: number[],
  ): Observable<any>[] {
    const requests: Observable<any>[] = [];
    // PROJECT INSTANCE UPDATE
    if (statusChanges) {
      requests.push(
        this.investmentScenariiService.mainScenario$.pipe(
          first(),
          switchMap((scenario) => {
            const instance = this.project?.instances?.find((i) => i.scenario?.id === scenario.id);
            return this.projectsService.updateProjectInstance(
              projectId,
              [{
                id: instance?.id,
                status: statusChanges,
                scenario: scenario.id,
                savings_start_at: instance?.savings_start_at || this.datePipe.transform(new Date(), "yyyy-MM-dd"),
              }],
            );
          }),
        ),
      );
    }

    // GAINS UPDATE
    if (gainsChanges) {
      requests.push(
        this.projectsService.updateProjectGains(
          projectId,
          gainsChanges
        ).pipe(first()),
      );
    }

    // INVESTMENTS AND INITIATIVE UPDATE
    if (investmentsChanges || initiativesChanges) {
      requests.push(
        this.projectsService.updateProjectInitiativesAndInvestments(
          projectId,
          this.form.get('investments').value,
          this.form.get('project_initiatives').value,
        ).pipe(first()),
      );
    }

    return requests?.length ? requests : [of(null)];
  }

  // Functions not implemented in this component
  public goToAudit(): void { }
  protected setUpAvailableValues(): void { }
}
